import RelatedArticlesComponent from "../../../../components/help/RelatedArticlesComponent.tsx";
import ComparingChart from "../../../../components/help/ComparingChart.tsx";
import {Link} from "react-router-dom";
import Routing from "../../../../Routing.ts";
import OrderAddressSearchComponent from "../../../../components/order/OrderAddressSearchComponent.tsx";
import {DispatchHighLightAddressSearchEvent} from "../../../../util/events.ts";
import Icon from "../../../../components/selfcare/Icon.tsx";
import IconArrow from "../../../../components/icons/IconArrow.tsx";

export default function UnitsOnWifiArticle() {
    return (
        <>
            <article className={'help-article-content'}>
                <div className={'article'}>
                    <div className={'text-box text-box--gray'}>
                        <p>
                            Når du skal vælge en internetforbindelse, støder du på begreber som Mbit, Mbps, megabyte og gigabyte.
                            Herunder får du en intro til internethastigheder og datamængder og en grundig gennemgang af de vigtigste begreber.
                        </p>
                    </div>
                </div>

                <div className={'article'}>
                    <div className={'paragraph'}>
                        <h2>Hvad er bits og bytes?</h2>
                        <p>
                            Bits og bytes er grundlæggende enheder for datamængder og hastigheder.
                            De betyder følgende:
                        </p>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    <strong>Bit (b):</strong>{' '}
                                    Den mindste enhed af data i en computer, der kan have én af to værdier: 0 eller 1.
                                    Ofte bruger man bit/s for at angive datahastigheder.
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>Byte (B):</strong>{' '}
                                    En gruppe på 8 bits.
                                    Én byte kan repræsentere 256 forskellige værdier (fra 00000000 til 11111111).
                                    Bytes bruges til datamængder/lagring.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className={'article'}>
                    <div className={'paragraph'}>
                        <h2>Datahastigheder</h2>
                        <ComparingChart className={'comparing-chart--data'}>
                            <tr>
                                <td>
                                    <p>1 Kbps (Kbit/s)</p>
                                </td>
                                <td>
                                    <p>1000 bps (bits per sekund)</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 Mbps (Mbit/s)</p>
                                </td>
                                <td>
                                    <p>1000 Kbps (Kbit/s)</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 Gbps (Gbit/s)</p>
                                </td>
                                <td>
                                    <p>1000 Mbps (Mbit/s)</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 Tbps (Tbit/s)</p>
                                </td>
                                <td>
                                    <p>1000 Gbps (Gbit/s)</p>
                                </td>
                            </tr>
                        </ComparingChart>

                        <h3>Hvad er Kbps? Og hvor mange Kbps er en Mbit?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Kbps står for kilobits per sekund og angiver, hvor mange kilobits (1.000 bits) der overføres per sekund.</p>
                            </li>
                            <li>
                                <p>1.000 kbps er 1 Mbit.</p>
                            </li>
                            <li>
                                <p> Kbps bruges sjældent i dag, da det er en lille måleenhed.</p>
                            </li>
                        </ul>

                        <h3>Hvad er Mbit og Mbps?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Mbit står for megabit, hvilket betyder en million bits.</p>
                            </li>
                            <li>
                                <p>Mbps betyder megabit per second og angiver en overførselshastighed, altså hvor mange megabit der bliver sendt i sekundet.</p>
                            </li>
                            <li>
                                <p>Mange anvender betegnelsen Mbit om internethastighed, f.eks. 1000 Mbit internet, men det er ikke helt korrekt &ndash; det burde skrives som 1000 Mbit/s eller Mbps.</p>
                            </li>
                            <li>
                                <p>Mbit og Mbps kan sammenlignes med km og km/t, hvor den ene enhed er en længde, og den anden en længde/mængde over tid.</p>
                            </li>
                            <li>
                                <p>Mbit er bare blevet en meget anvendt betegnelse for internethastighed, og det er forøvrigt også en betegnelse, som vi, og mange andre internetudbydere, bruger om internethastighed ved køb af abonnementer.</p>
                            </li>
                        </ul>

                        <h3>Hvor mange Mbps er 1000 Mbit?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    Det er som at spørge, hvor mange km/t 1000 km. er uden at kende hastigheden.
                                    Det er umuligt at svare på, da der mangler en ukendt variabel.
                                </p>
                            </li>
                            <li>
                                <p>
                                    Mbps betegner, hvor mange megabit per sekund, der bliver sendt, og 1000 Mbit er blot en datamængde.
                                    Hvis der stod 1000 Mbit/s, betyder det, at der bliver sendt 1000 megabit per sekund.
                                </p>
                            </li>
                        </ul>

                        <h3>Hvor mange Mbps skal jeg bruge til Netflix?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    Ifølge Netflix&rsquo; egne retningslinjer skal du anvende disse hastigheder til følgende opløsninger:
                                </p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>Lav kvalitet (480p, SD) &rarr; 3 Mbps (Mbit/s)</p>
                                    </li>
                                    <li>
                                        <p>HD (720p) &rarr; 5 Mbps  (Mbit/s)</p>
                                    </li>
                                    <li>
                                        <p>Full HD (1080p) &rarr; 10 Mbps (Mbit/s)</p>
                                    </li>
                                    <li>
                                        <p>4K UHD (2160p) &rarr; 15-25 Mbps (Mbit/s)</p>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <p>Disse hastigheder gælder per enhed, så hvis flere streamer eller bruger internettet samtidig, kræver det en højere hastighed end angivet ovenfor.</p>
                            </li>
                        </ul>

                        <h3>Hvad betyder 1000/100 Mbit?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>1000/100 Mbit internet betyder, at din internetforbindelse har en download-hastighed på 1000 megabit per sekund (Mbit/s) og en upload-hastighed på 100 megabit per sekund (Mbit/s).</p>
                            </li>
                            <li>
                                <p>1000 Mbit/s internet er den hurtigste hastighed, vi kan levere og også den mest optimale, hvis du har mange enheder på dit wifi samtidig.</p>
                            </li>
                            <li>
                                <p>Du kan læse mere om download- og upload-hastighed i vores hastighedstest-artikel herunder:
                                </p>
                                <div className={'button-container'}>
                                    <Link
                                        className={'button button--montserrat with-icon teal'}
                                        to={'/hjaelp/internet-og-wifi/hastighedstest'}
                                    >
                                        <span className={'text'}>Hastighedstest</span>
                                        <Icon component={IconArrow}/>
                                    </Link>
                                </div>
                            </li>
                        </ul>

                        <h3>Hvor mange Mbit skal jeg bruge?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    Du kan læse om de forskellige internetforbindelser, hastigheder og forbrug i artiklen herunder:
                                </p>
                                <div className={'button-container'}>
                                    <Link
                                        className={'button button--montserrat with-icon teal'}
                                        to={'/hjaelp/internet-og-wifi/hvilket-internet-skal-jeg-vaelge'}
                                    >
                                        <span className={'text'}>Hvilket Internet skal jeg vælge?</span>
                                        <Icon component={IconArrow}/>
                                    </Link>
                                </div>
                            </li>
                        </ul>

                        <h3>Hvad er Gbps? Og hvor mange megabit er en gigabit?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Gbps står for gigabits per second.</p>
                            </li>
                            <li>
                                <p>1 Gbps er 1000 Mbit/s.</p>
                            </li>
                        </ul>

                        <h3>Hvad er Tbps?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Tbps står for terabits per second og bruges i ekstremt hurtige netværk, som datacentre og satellitnetværk, og er som regel ikke en hastighedsenhed, der anvendes af private brugere.</p>
                            </li>
                            <li>
                                <p>1 Tbps er 1000 Gbps.</p>
                            </li>
                        </ul>

                        <h3>Hvilke datahastigheder/Mbit/s kan I tilbyde?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    Hastigheden, du kan få, afhænger af din adresse og hvilken internetteknologi, du kan få (hvad enten det er internet via{' '}
                                    <Link to={Routing.Broadband.Fiber}>fiber</Link>,{' '}
                                    <Link to={Routing.Broadband.TDC5G}>5G</Link> eller{' '}
                                    <Link to={Routing.Broadband.Coax}>kabel-tv-stikket</Link>.
                                </p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>Hvis du vil have fibernet, kan du vælge mellem 100 og 1000 Mbit.</p>
                                    </li>
                                    <li>
                                        <p>
                                            Hvis du ønsker 5G-internet, afhænger hastigheden af din adresse &ndash; den kan du{' '}
                                            <button
                                                className={'link'}
                                                onClick={() => DispatchHighLightAddressSearchEvent(true)}
                                            >
                                                slå op her
                                            </button>.
                                        </p>
                                    </li>
                                    <li>
                                        <p>Med internet via kabel-tv-stikket kan du få 300/60 Mbit eller 1000/500 Mbit.</p>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <p>
                                    Der er ingen databegrænsning, når du køber internet hos os.
                                    Du skal blot forholde dig til den internethastighed, du ønsker og kan få på din adresse.
                                </p>
                            </li>
                            <li>
                                <p>Du kan læse mere om download og upload i vores <Link to={'/hjaelp/internet-og-wifi/hastighedstest'}>hastighedstest-artikel</Link> under &ldquo;Hvad betyder download og upload?&rdquo;.</p>
                            </li>
                            <li>
                                <p>
                                    <button
                                        className={'link'}
                                        onClick={() => DispatchHighLightAddressSearchEvent(true)}
                                    >
                                        Slå altid din adresse op
                                    </button>{' '}
                                    for at se hvilket produkt og hastighed, vi kan tilbyde på din adresse.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className={'article'}>
                    <div className={'paragraph'}>
                        <h2>Datamængder</h2>
                        <ComparingChart className={'comparing-chart--data'}>
                            <tr>
                                <td>
                                    <p>1 byte</p>
                                </td>
                                <td>
                                    <p>8 bits</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 kilobyte (KB)</p>
                                </td>
                                <td>
                                    <p>1024 bytes</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 megabyte (MB)</p>
                                </td>
                                <td>
                                    <p>1024 kilobytes (KB)</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 gigabyte (GB)</p>
                                </td>
                                <td>
                                    <p>1024 megabytes (MB)</p>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <p>1 terabyte (TB)</p>
                                </td>
                                <td>
                                    <p>1024 gigabytes (GB)</p>
                                </td>
                            </tr>
                        </ComparingChart>

                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Vi sælger internetabonnementer baseret på hastigheder &ndash; ikke datamængder.</p>
                            </li>
                            <li>
                                <p>Du får altid fri data med et internetabonnement hos os.</p>
                            </li>
                            <li>
                                <p>Nedenstående afsnit er blot til at forklare sammenhængen mellem datamængder og hastigheder.</p>
                            </li>
                            <li>
                                <p>
                                    Ofte bliver datamængder konverteret til hele tusinde, men det er ikke 100% korrekt.
                                    F.eks. Står der mange steder, at 1 KB = 1000 bytes, men det faktuelle korrekte er 1024 bytes.
                                </p>
                            </li>
                        </ul>

                        <h3>Hvad er kilobyte?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>En kilobyte (KB) er en enhed for datamængde og bruges ofte til at måle små filer som tekstfiler og billeder i lav opløsning.</p>
                            </li>
                            <li>
                                <p>1 KB er 1.024 bytes.</p>
                            </li>
                        </ul>

                        <h3>Hvad er megabyte? Og hvor mange kilobyte er en megabyte?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Megabyte (MB) er en enhed for datamængde.</p>
                            </li>
                            <li>
                                <p>1 MB er 1.024 KB.</p>
                            </li>
                            <li>
                                <p>En MP3-fil i god kvalitet (320 kbps) fylder ca. 5 MB pr. minut og et foto i høj opløsning kan fylde 2-10 MB.</p>
                            </li>
                        </ul>

                        <h3>Hvad er gigabyte? Og hvor mange megabyte går der på en gigabyte?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>En gigabyte (GB) er en enhed for datamængde.</p>
                            </li>
                            <li>
                                <p>1 GB er 1.024 MB.</p>
                            </li>
                        </ul>

                        <h3>Hvor meget er 1 GB data?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Med 1 GB data kan du lave forskellige aktiviteter på internettet, f.eks.:</p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>Musikstreaming af høj kvalitet (320 kbps) &rarr; ca. 7 timer</p>
                                    </li>
                                    <li>
                                        <p>Video streaming i Full HD på diverse platforme (1080p) &rarr; ca. 1 time per GB</p>
                                    </li>
                                    <li>
                                        <p>Browsing og e-mails &rarr; 600-1.000 sider per GB</p>
                                    </li>
                                </ul>
                            </li>
                        </ul>

                        <h3>Hvor mange gigabyte fylder en film?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>Filmens størrelse i gigabyte (GB) afhænger af opløsning, format og bitrate.</p>
                            </li>
                            <li>
                                <p>
                                    Typiske filstørrelser på Netflix-streaming (gennemsnit pr. time):
                                </p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>SD (480p) &rarr; ca. 1 GB/t</p>
                                    </li>
                                    <li>
                                        <p>HD (720p) &rarr; ca. 3 GB/t</p>
                                    </li>
                                    <li>
                                        <p>Full HD (1080p) &rarr; ca. 5 GB/t</p>
                                    </li>
                                    <li>
                                        <p>4K UHD (2160p) &rarr; ca. 7-10 GB/t</p>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <p>
                                    Downloadede film kan fylde mere, især hvis de er ukomprimerede Blu-ray-versioner (op til 50 GB for en 4K-film).
                                </p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>
                                            Hvis du f.eks. vil downloade en film, som fylder 5 GB med en internethastighed på 100 Mbit/s, tager det 1 min. og 20 sek.
                                            Med en internethastighed på 1000 Mbit/s tager det 8 sek.
                                        </p>
                                    </li>
                                </ul>
                            </li>
                        </ul>

                        <h3>Hvor mange GB bruger man om måneden?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    Det afhænger ligesom mængden af Mbit af, hvor meget du bruger internettet.
                                </p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>
                                            <strong>Let brug (5-15 GB/md.):</strong><br/>
                                            Mail og sociale medier, websurfing, let musikstreaming (lav kvalitet) og e-mails uden vedhæftning.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            <strong>Moderat brug (20-50 GB/md.):</strong><br/>
                                            Daglig streaming af musik (normalt 160 kbps eller højere kvalitet), YouTube eller Netflix i lav til medium kvalitet (SD og HD), nogle videomøder og videoopkald, let online gaming og brug af apps og downloads.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            <strong>Højt brug (50-100 GB/md.):</strong><br/>
                                            Daglig streaming af video i HD (720p/1080p), Netflix og YouTube i høj kvalitet (Full HD), gaming (online spil og downloads), store softwareopdateringer og downloads og 4K video streaming (YouTube, Netflix, etc.).
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            <strong>Tungt brug (100+ GB/md.):</strong><br/>
                                            Streaming af 4K-videoer, meget gaming (store spil og online spil), store filoverførsler og downloads, og flere personer der deler dataforbruget i husstanden.
                                        </p>
                                    </li>
                                </ul>
                            </li>
                        </ul>

                        <h3>Hvad er en terabyte? Og hvor mange gigabyte er en terabyte?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    En terabyte (TB) er en enhed for datamængde, der bruges til at måle store mængder digital lagring, såsom harddiske, cloud-lagring og store filer.
                                </p>
                            </li>
                            <li>
                                <p>
                                    1 TB er 1.024 GB.
                                </p>
                            </li>
                        </ul>

                        <h3>Sælger I mobilabonnementer?</h3>
                        <ul className={'list list--bullet'}>
                            <li>
                                <p>
                                    Nej, vi sælger ikke mobilabonnementer, men du kan købe <Link to={Routing.Broadband.TDC5G}>5G-internet</Link> hos os.
                                </p>
                                <ul className={'list list--bullet'}>
                                    <li>
                                        <p>
                                            Det vil sige, at du får en router med simkort i, og så tilslutter du dine enheder til wifi fra routeren.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Du køber ikke en datamængde (GB) i forbindelse med dette, men derimod en hastighed (Mbit/s).
                                        </p>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className={'article'}>
                    <div className={'paragraph'}>
                        <h2>
                            Hvor hurtigt internet kan jeg få?
                        </h2>
                        <p>
                            Slå din adresse op herunder for at se, hvilke hastigheder vi kan tilbyde dig:
                        </p>
                        <OrderAddressSearchComponent/>
                    </div>
                </div>
            </article>

            <RelatedArticlesComponent
                articles={[
                    {
                        "category": "internet-og-wifi",
                        "article": "info-om-internet-og-wifi",
                        "name": "Godt at vide om wifi og internet"
                    },
                    {
                        "category": "internet-og-wifi",
                        "article": "hastighedstest",
                        "name": "Hastighedstest"
                    },
                    {
                        "category": "internet-og-wifi",
                        "article": "forventninger-til-wifi",
                        "name": "Hvad kan jeg forvente af mit wifi?"
                    }
                ]}
            />
        </>
    )
}
